import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ArrowLeft, ChevronDown } from 'lucide-react';

const faqData = [
  {
    question: "What is A1C Survival Plan?",
    answer: "A1C Survival Plan is a journal-centered app that helps you organize daily life with diabetes — including food, fitness, medications, hydration, mental health, and reflections — in one connected place."
  },
  {
    question: "Is this app medical advice?",
    answer: "No. A1C Survival Plan does not provide medical advice, diagnoses, or treatment. It's a personal organization and reflection tool designed to help you better understand patterns in your daily life."
  },
  {
    question: "Who is this app for?",
    answer: "This app is for anyone living with diabetes who wants a clearer, more organized way to track daily habits and understand how they connect over time."
  },
  {
    question: "Do I need to log everything for the app to be useful?",
    answer: "No. A1C Survival Plan is designed to be flexible. Even partial entries can provide meaningful insights. The goal is consistency, not perfection."
  },
  {
    question: "How is this different from other diabetes or tracking apps?",
    answer: "Most apps focus on one area — glucose, food, or fitness. A1C Survival Plan brings everything together around a journal, so your daily life is captured in context, not fragments."
  },
  {
    question: "Can I share or export my information?",
    answer: "Yes. You can view, summarize, and export your information to share with healthcare providers or keep for your personal records."
  },
  {
    question: "Is my data private?",
    answer: "Yes. Your data belongs to you. We do not sell or share personal health data, and privacy is built into the app from the start."
  },
  {
    question: "Does this app connect to Apple Health or other devices?",
    answer: "Some integrations may be available or added over time. You control what data is shared and whether integrations are enabled."
  },
  {
    question: "What if I miss a day or forget to log something?",
    answer: "That's okay. A1C Survival Plan is designed to reflect real life. You can log entries later or simply move forward without penalty."
  },
  {
    question: "Is this app free?",
    answer: "Pricing and feature availability may vary. Any paid features will always be clearly explained before purchase."
  }
];

const FAQItem = ({ question, answer, isOpen, onClick, index }) => {
  return (
    <div className="border-b border-slate-200 last:border-b-0">
      <button
        onClick={onClick}
        className="w-full py-6 flex items-start justify-between text-left group"
      >
        <div className="flex items-start gap-4">
          <span className="text-teal-500 font-semibold text-lg">{index + 1}.</span>
          <span className="text-lg font-medium text-slate-800 group-hover:text-teal-600 transition-colors">
            {question}
          </span>
        </div>
        <ChevronDown 
          className={`w-5 h-5 text-slate-400 flex-shrink-0 mt-1 transition-transform duration-300 ${
            isOpen ? 'rotate-180 text-teal-500' : ''
          }`}
        />
      </button>
      <div 
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-96 pb-6' : 'max-h-0'
        }`}
      >
        <p className="text-slate-600 leading-relaxed pl-9">
          {answer}
        </p>
      </div>
    </div>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-800">A1C Survival Plan</span>
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 text-slate-600 hover:text-teal-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-800 mb-4">Frequently Asked Questions</h1>
          <p className="text-lg text-slate-600">
            Find answers to common questions about A1C Survival Plan
          </p>
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
          {faqData.map((faq, index) => (
            <FAQItem
              key={index}
              index={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onClick={() => toggleFAQ(index)}
            />
          ))}
        </div>

        {/* Contact section */}
        <div className="mt-12 text-center">
          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-3xl p-8">
            <h3 className="text-xl font-semibold text-slate-800 mb-3">
              Still have questions?
            </h3>
            <p className="text-slate-600 mb-4">
              We're here to help. Reach out to our support team.
            </p>
            <a 
              href="mailto:support@a1csurvivalplan.com"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium rounded-xl hover:from-teal-400 hover:to-cyan-400 transition-all duration-300 shadow-lg shadow-teal-200"
            >
              Contact Support
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-50 border-t border-slate-100 mt-16">
        <div className="max-w-4xl mx-auto px-6 py-8 text-center">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} A1C Survival Plan. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default FAQ;
